import {useMemo} from 'react';
import {kebabCase} from 'lodash';

import {
  useHomeAdaptiveSubjectsQuery,
  useHomeAllAdaptiveSubjectsQuery
} from '../HomeAdaptiveSubjects.queries.generated';

export interface HomeAdaptiveSubject {
  id: string;
  name: string;
  urlSlug: string;
  /** kebab-cased curriculum area (e.g. "math"), keyed to the --color-background-domain-<slug> token. */
  curriculumAreaSlug: string;
}

interface HomeAdaptiveSubjectsResult {
  subjects: HomeAdaptiveSubject[];
  loading: boolean;
}

const toAdaptiveSubject = (subject: {
  id: string;
  name: string;
  urlSlug: string;
  curriculumArea?: {name: string} | null;
}): HomeAdaptiveSubject => ({
  id: subject.id,
  name: subject.name,
  urlSlug: subject.urlSlug,
  curriculumAreaSlug: subject.curriculumArea?.name
    ? kebabCase(subject.curriculumArea.name)
    : 'default'
});

/**
 * Adaptive subjects to suggest on Home, personalized by the teacher's curriculum area(s). Falls back to
 * the full adaptive catalog when the teacher's areas map to none OR the personalized query fails
 */
export const useHomeAdaptiveSubjects = (): HomeAdaptiveSubjectsResult => {
  const {data, loading, error} = useHomeAdaptiveSubjectsQuery({
    fetchPolicy: 'cache-and-network'
  });
  const personalized = useMemo(
    () => (data?.subjects?.results ?? []).map(toAdaptiveSubject),
    [data]
  );
  const needsFallback = personalized.length === 0 && (data !== undefined || Boolean(error));

  const {data: fallbackData, loading: fallbackLoading} = useHomeAllAdaptiveSubjectsQuery({
    skip: !needsFallback
  });
  const fallbackSubjects = useMemo(
    () => (fallbackData?.subjects?.results ?? []).map(toAdaptiveSubject),
    [fallbackData]
  );

  if (needsFallback) {
    return {subjects: fallbackSubjects, loading: fallbackLoading};
  }

  return {subjects: personalized, loading};
};

/**
 * The full adaptive-subject catalog (every non-hidden adaptive subject), independent of the teacher's
 * access or personalization. Use where the complete list is wanted, e.g. the Skill Paths subject filter.
 */
export const useAllAdaptiveSubjects = (): HomeAdaptiveSubjectsResult => {
  const {data, loading} = useHomeAllAdaptiveSubjectsQuery({fetchPolicy: 'cache-first'});
  const subjects = useMemo(() => (data?.subjects?.results ?? []).map(toAdaptiveSubject), [data]);
  return {subjects, loading};
};
